import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import './DeleteAccount.css';

const DeleteAccount = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const getSubmitErrorMessage = (error) => {
    switch (error?.code) {
      case 'permission-denied':
        return 'This request is being blocked by Firestore rules right now. Try again after the latest rules are deployed, or email info@clientellapps.com.';
      case 'unavailable':
        return 'Firestore is temporarily unavailable. Please try again in a moment or email info@clientellapps.com.';
      case 'failed-precondition':
        return 'Firestore is not fully set up for this request yet. Please try again later or email info@clientellapps.com.';
      default:
        return 'We could not submit your request right now. This may be a Firestore setup issue. Please try again or email info@clientellapps.com.';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitState('idle');
    setErrorMessage('');

    try {
      await addDoc(collection(db, 'deleteRequests'), {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        status: 'pending',
        source: 'website',
        createdAt: serverTimestamp(),
      });

      setFullName('');
      setEmail('');
      setSubmitState('success');
    } catch (error) {
      console.error('Error creating delete request:', error);
      setErrorMessage(getSubmitErrorMessage(error));
      setSubmitState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="delete-account-page">
      <div className="delete-account-card">
        <p className="delete-account-eyebrow">Account Help</p>
        <h1 className="delete-account-title">Delete Your Account</h1>
        <p className="delete-account-copy">
          Submit the form below using the email connected to your account. We will email you within 5 to 7 business days. Once you confirm the deletion request, we will delete your account.
        </p>

        <p className="delete-account-copy">
          For a quicker account deletion option, use the Account page inside the Mickey Subs app.
        </p>

        <div className="delete-account-panel">
          <h2 className="delete-account-subtitle">How this works</h2>
          <ul className="delete-account-list">
            <li>Submit the email address tied to your Mickey Subs account.</li>
            <li>We will review your request and contact you within 5 to 7 business days.</li>
            <li>After you confirm the request, we will delete your account and associated data.</li>
          </ul>
        </div>

        <form className="delete-account-form" onSubmit={handleSubmit}>
          <label className="delete-account-field">
            <span className="delete-account-label">Full Name</span>
            <input
              className="delete-account-input"
              type="text"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Your full name"
              autoComplete="name"
            />
          </label>

          <label className="delete-account-field">
            <span className="delete-account-label">Email Address</span>
            <input
              className="delete-account-input"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="email@example.com"
              autoComplete="email"
              required
            />
          </label>

          <button className="delete-account-button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>

        {submitState === 'success' && (
          <p className="delete-account-feedback delete-account-feedback--success">
            Your request was submitted. We will reach out to the email connected to the account within 5 to 7 business days.
          </p>
        )}

        {submitState === 'error' && (
          <p className="delete-account-feedback delete-account-feedback--error">
            {errorMessage}
          </p>
        )}

        <p className="delete-account-note">
          Questions about how we handle your information? Review our <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </div>
    </section>
  );
};

export default DeleteAccount;